import React, { useState, useEffect, useMemo } from 'react';
import useEquipmentData from 'hooks/useEquipmentData';
import EquipmentDropdownBase from './components/DropdownBase';
import {
  EquipmentDropdownProps,
  DropdownGroupItem,
  EquipmentItem
} from './types';
import './style.css';

const formatEquipmentOptions = (
  equipmentItems: EquipmentItem[]
): DropdownGroupItem[] => {
  if (!equipmentItems || equipmentItems.length === 0) {
    return [];
  }

  const items = equipmentItems.map(item => ({
    key: item.id,
    value: item,
    label: item.name
  }));

  return [
    {
      key: 'equipment-group',
      label: 'Equipment',
      items
    }
  ];
};

/**
 * Equipment dropdown component
 */
const EquipmentDropdown: React.FC<EquipmentDropdownProps> = ({
  id,
  onChange,
  value,
  title,
  bottomButtonText,
  bottomButtonIcon,
  allowSearch = false,
  hideIcon = false,
  mode = 'single',
  placeholder = ''
}) => {
  const [mixedItems, setMixedItems] = useState<DropdownGroupItem[]>([]);
  const { equipments, loading, refetchEquipment } = useEquipmentData();

  useEffect(() => {
    if (equipments) {
      const formatted = formatEquipmentOptions(equipments);
      setMixedItems(formatted);

      // If value has placeholder equipment, replace with real data
      if (value && !loading) {
        const valueArray = Array.isArray(value) ? value : [value];
        const needsUpdate = valueArray.some(
          item => item?.name === 'Loading...'
        );
        if (needsUpdate) {
          const updatedValue = valueArray
            .map(item => {
              if (item?.id) {
                const realEquipment = equipments.find(eq => eq.id === item.id);
                if (realEquipment) {
                  return {
                    id: realEquipment.id,
                    name: realEquipment.name,
                    type: realEquipment.type
                  };
                }
              }
              return item;
            })
            .filter(Boolean);
          onChange(updatedValue);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipments, loading, value]);

  // Convert single item to array for EquipmentDropdownBase
  const normalizedValue = useMemo(() => {
    if (!value) return [];
    const normalized = Array.isArray(value) ? value : [value];
    return normalized;
  }, [value]);

  // Handle onChange to maintain the expected return type
  const handleChange = (newValue: EquipmentItem[]) => {
    if (mode === 'multiple') {
      onChange(newValue);
    }

    if (mode === 'single') {
      const currentIds = normalizedValue.map(item => item.id) || [];
      const newItems = newValue.filter(item => !currentIds.includes(item.id));

      if (newItems.length > 0) {
        onChange(newItems[0]); // if there's a new item, pass only that item to onChange
      } else if (newValue.length < normalizedValue.length) {
        onChange([]); // if an item was removed
      }
    }
  };

  return (
    <EquipmentDropdownBase
      id={id || 'equipment-dropdown'}
      onChange={handleChange}
      value={normalizedValue}
      title={title}
      bottomButtonText={bottomButtonText}
      bottomButtonIcon={bottomButtonIcon}
      allowSearch={allowSearch}
      items={mixedItems}
      hideIcon={hideIcon}
      mode={mode}
      loading={loading}
      refetchEquipment={refetchEquipment}
      placeholder={placeholder}
    />
  );
};

export default EquipmentDropdown;
